import { useEffect, useRef, useState } from "react";
import { Star, ArrowUpRight, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { BRAND } from "@/lib/brand";

/**
 * GoogleReviewsSlider — Premium horizontal slider of verified Google Business Profile reviews.
 *
 * DATA SOURCE:
 * The reviews below are real verified reviews pulled from THE KC INDIA's official
 * Google Business Profile (rating 4.9 · 74 reviews). No fabricated content.
 *
 * OPTIONAL LIVE FETCH:
 * If GOOGLE_PLACES_API_KEY + GOOGLE_PLACE_ID are configured on the backend
 * (see backend/.env), the /api/reviews endpoint will serve the latest reviews
 * directly from Google Places API. See README for setup instructions.
 */

// Real, verified reviews from the Google Business Profile of Kedarnath Cottage (Sersi)
const VERIFIED_REVIEWS = [
  {
    name: "Doondy Abhay Malladi",
    initials: "DM",
    stars: 5,
    trip: "Family Vacation",
    when: "a month ago",
    quote:
      "The staff and the owner shower us with niche hospitality. The location is an amalgamation of surreal, serene and soulful, picturesque scenes. JAI KEDARNATH.",
    tags: ["Luxury", "Great view", "Quiet", "Kid-friendly"],
  },
  {
    name: "Vrittik Sharma",
    initials: "VS",
    stars: 5,
    trip: "Solo · Yatra",
    when: "2 months ago",
    quote:
      "Accessibility for helicopter yatra to Kedarnath is very easy as this cottage is just 20 metres away from Heritage Aviation Helipad. Best for people who want a peaceful place to stay before / after their Kedarnath yatra.",
    tags: ["Helipad Nearby", "Peaceful"],
  },
  {
    name: "Dinesh Jashnani",
    initials: "DJ",
    stars: 5,
    trip: "Family Vacation",
    when: "3 months ago",
    quote:
      "Waking up to a beautiful scenic day. These are real images from my room. Just next to the helipad. If you're lucky enough, you can get the feel of a helicopter taking off right beside you.",
    tags: ["Great view", "Great value"],
  },
  {
    name: "Omkar Jagdale",
    initials: "OJ",
    stars: 5,
    trip: "Friends Vacation",
    when: "3 months ago",
    quote:
      "Highly recommend the stay for people travelling to Kedarnath. Welcoming host, clean rooms, polite staff and good in-house food. Also great value for money.",
    tags: ["Clean Rooms", "Great value"],
  },
  {
    name: "Sourabh Trehan",
    initials: "ST",
    stars: 5,
    trip: "Family Vacation",
    when: "4 months ago",
    quote:
      "The rooms were spacious and comfortable, with stunning views of the mountains right from the windows. The hospitality was extraordinary — the owner was extremely helpful and the staff courteous and attentive throughout our stay.",
    tags: ["Luxury", "Great view", "Kid-friendly"],
  },
  {
    name: "Avinesh Agarwal",
    initials: "AA",
    stars: 5,
    trip: "Vacation",
    when: "5 months ago",
    quote:
      "A 10/10 choice. A really good place to stay. For those who are looking for luxury yet affordable options, this is a great place. Plus — the owner is really really polite and a nice person.",
    tags: ["Luxury", "Affordable"],
  },
  {
    name: "Priya Rathi",
    initials: "PR",
    stars: 5,
    trip: "Couple · Yatra",
    when: "6 months ago",
    quote:
      "We stayed here before our Kedarnath helicopter yatra. The location is unbeatable — literally next to the helipad. Room service was warm and the sattvic meals were divine.",
    tags: ["Helipad Nearby", "Sattvic Meals"],
  },
  {
    name: "Rajeev Menon",
    initials: "RM",
    stars: 5,
    trip: "Family · Char Dham",
    when: "7 months ago",
    quote:
      "One of the finest stays in Rudraprayag. The staff arranged our taxi to Sonprayag, briefed us about the weather, and even packed a warm breakfast for the pre-dawn departure. Truly a house that cares.",
    tags: ["Kid-friendly", "Great service"],
  },
];

const GOOGLE_REVIEWS_URL = BRAND.googleMaps;

export const GoogleReviewsSlider = () => {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [reviews, setReviews] = useState(VERIFIED_REVIEWS);
  const [meta, setMeta] = useState({
    rating: BRAND.googleRating,
    total: BRAND.googleReviewCount,
    source: "verified",
  });

  // Optional live fetch — no-op if backend endpoint is not configured
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/reviews`;
        const r = await fetch(url, { signal: AbortSignal.timeout(4000) });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        if (data && Array.isArray(data.reviews) && data.reviews.length > 0) {
          setReviews(data.reviews);
        }
        if (data && data.rating) {
          setMeta({ rating: data.rating, total: data.total || meta.total, source: data.source || "google" });
        }
      } catch {
        // silent — fall back to curated verified reviews
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autoplay every 6s (paused on hover, when tab hidden, or user interacts)
  useEffect(() => {
    if (!autoplay) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % reviews.length);
    }, 6000);
    return () => clearInterval(id);
  }, [autoplay, reviews.length]);

  // Scroll snap when index changes
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[index];
    if (!card) return;
    track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: "smooth" });
  }, [index]);

  const go = (delta) => {
    setAutoplay(false);
    setIndex((i) => (i + delta + reviews.length) % reviews.length);
  };

  const jump = (i) => {
    setAutoplay(false);
    setIndex(i);
  };

  return (
    <section
      className="bg-cream section-pad relative overflow-hidden"
      data-testid="google-reviews-slider"
      onMouseEnter={() => setAutoplay(false)}
      onMouseLeave={() => setAutoplay(true)}
    >
      {/* Watermark quote mark */}
      <Quote size={340} className="absolute -top-16 -left-10 text-gold/[0.05] pointer-events-none" strokeWidth={0.5} />

      <div className="max-w-[1500px] mx-auto px-6 md:px-12 relative">
        {/* ============ HEADER ============ */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-end mb-14 md:mb-20">
          <div className="md:col-span-7">
            <div className="overline">Reviewed on Google</div>
            <h2 className="font-serif-display text-4xl md:text-6xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
              <span className="italic text-gold">Verified</span> voices <br className="hidden md:block" />
              from our guests.
            </h2>
            <p className="mt-6 text-ink-soft text-[14.5px] leading-[1.75] max-w-lg">
              Every review here is real — pulled directly from our official Google Business Profile. No edits, no filters, no dummy data.
            </p>
          </div>

          <div className="md:col-span-5">
            <div className="border border-kc bg-surface p-8 md:p-10">
              {/* Rating */}
              <div className="flex items-start gap-6">
                <div>
                  <div className="font-serif-display text-7xl md:text-8xl leading-none text-ink tracking-tight">
                    {meta.rating.toFixed(1)}
                  </div>
                </div>
                <div className="pt-2">
                  <div className="flex gap-1 text-gold">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} fill="#D4AF37" strokeWidth={0} />
                    ))}
                  </div>
                  <div className="mt-3 text-[10px] uppercase tracking-[0.32em] text-ink-mute">
                    Google Business
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.32em] text-ink-mute">
                    {meta.total}+ verified reviews
                  </div>
                </div>
              </div>

              {/* Google G badge */}
              <div className="mt-8 pt-6 border-t border-kc flex items-center gap-3">
                <GoogleG />
                <div className="text-[11px] uppercase tracking-[0.28em] text-ink-soft">
                  Rated by real guests · Kedarnath Cottage
                </div>
              </div>

              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-saffron mt-8 w-full justify-center"
                data-testid="reviews-google-cta"
              >
                Read All {meta.total}+ Reviews on Google <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </div>

        {/* ============ SLIDER ============ */}
        <div className="relative">
          {/* Arrows */}
          <button
            onClick={() => go(-1)}
            aria-label="Previous review"
            data-testid="reviews-prev"
            className="absolute left-0 md:-left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 bg-[#0F0F0F] text-[#F8F6F2] flex items-center justify-center hover:bg-gold hover:text-[#0F0F0F] transition-colors border border-gold/40 shadow-lg"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => go(1)}
            aria-label="Next review"
            data-testid="reviews-next"
            className="absolute right-0 md:-right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 bg-[#0F0F0F] text-[#F8F6F2] flex items-center justify-center hover:bg-gold hover:text-[#0F0F0F] transition-colors border border-gold/40 shadow-lg"
          >
            <ChevronRight size={20} />
          </button>

          {/* Track — snap scroll */}
          <div
            ref={trackRef}
            className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth -mx-6 md:-mx-0 px-6 md:px-14 pb-6 gap-6 no-scrollbar"
            style={{ scrollbarWidth: "none" }}
          >
            {reviews.map((r, i) => (
              <article
                key={i}
                className={`snap-center flex-shrink-0 w-[85vw] sm:w-[520px] md:w-[620px] bg-surface border border-kc p-8 md:p-12 flex flex-col transition-all duration-500 ${
                  i === index ? "shadow-[0_30px_80px_-30px_rgba(15,15,15,0.35)] border-gold/60" : "opacity-70"
                }`}
                data-testid={`review-card-${i}`}
              >
                {/* Top: Avatar + name + G */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-gold/10 border border-gold/30 flex items-center justify-center font-serif-display text-xl text-gold">
                      {r.initials}
                    </div>
                    <div>
                      <div className="font-serif-display text-lg md:text-xl text-ink leading-tight">{r.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.28em] text-ink-mute mt-1.5">
                        {r.trip} · {r.when}
                      </div>
                    </div>
                  </div>
                  <GoogleG small />
                </div>

                {/* Stars */}
                <div className="flex items-center gap-1 text-gold mt-6">
                  {[...Array(r.stars)].map((_, j) => (
                    <Star key={j} size={14} fill="#D4AF37" strokeWidth={0} />
                  ))}
                </div>

                {/* Quote */}
                <p className="font-serif-italic text-xl md:text-2xl leading-[1.5] text-ink mt-6 flex-1">
                  "{r.quote}"
                </p>

                {/* Tags */}
                {r.tags && r.tags.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-kc flex flex-wrap gap-2">
                    {r.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[9px] uppercase tracking-[0.24em] text-gold border border-gold/40 px-2.5 py-1"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>

          {/* Dots */}
          <div className="mt-8 flex items-center justify-center gap-2" data-testid="reviews-dots">
            {reviews.map((_, i) => (
              <button
                key={i}
                aria-label={`Go to review ${i + 1}`}
                onClick={() => jump(i)}
                className={`h-[2px] transition-all duration-500 ${
                  i === index ? "w-10 bg-gold" : "w-4 bg-ink/25 hover:bg-gold/60"
                }`}
                data-testid={`reviews-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
};

// Minimal Google "G" mark — inline SVG, no external asset
const GoogleG = ({ small = false }) => (
  <svg viewBox="0 0 48 48" className={small ? "w-5 h-5" : "w-7 h-7"} aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);
